import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getUsers, updateUser, deleteUser, creditUserWallet
} from '../../api';
import {
  PageHeader, StatusBadge, Table, Pagination, SearchInput, EmptyState, TableSkeleton
} from '../../components/ui';
import OverviewBar from '../../components/ui/OverviewBar';
import ExportButton from '../../components/ui/ExportButton';
import Modal from '../../components/ui/Modal';
import { formatDate } from '../../utils/format';
import {
  Eye, Ban, Wallet, RefreshCw, CheckCircle, Trash2, Edit,
  Users, UserCheck, UserX, Clock, X, Star
} from 'lucide-react';
import toast from 'react-hot-toast';

type UserRow = {
  _id: string; phone: string; name?: string; email?: string;
  language: string; isProfileComplete: boolean;
  isActive: boolean; isBlocked: boolean; blockedReason?: string;
  gender?: string; dateOfBirth?: string; language2?: string;
  createdAt: string; lastActiveAt: string;
};

type PageData = { data: UserRow[]; pagination?: any };

const USER_COLS = [
  { key: '_id', label: 'ID' }, { key: 'name', label: 'Name' },
  { key: 'phone', label: 'Phone' }, { key: 'email', label: 'Email' },
  { key: 'language', label: 'Language' }, { key: 'gender', label: 'Gender' },
  { key: 'isProfileComplete', label: 'Profile Complete' },
  { key: 'isActive', label: 'Active' }, { key: 'isBlocked', label: 'Blocked' },
  { key: 'createdAt', label: 'Joined' }, { key: 'lastActiveAt', label: 'Last Active' },
];

const LIMITS = [10, 20, 50, 100];
const LANGS = ['en', 'hi', 'ta', 'te', 'ml', 'kn', 'mr', 'bn', 'gu', 'pa'];

export default function UsersPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [langFilter, setLangFilter] = useState('');
  const [profileFilter, setProfileFilter] = useState('');
  const [genderFilter, setGenderFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sort, setSort] = useState('createdAt');
  const [order, setOrder] = useState<'desc' | 'asc'>('desc');

  useEffect(() => {
    setStatusFilter(searchParams.get('status') || '');
    setPage(1);
  }, [searchParams]);

  const [editUser, setEditUser] = useState<UserRow | null>(null);
  const [creditModal, setCreditModal] = useState<{ userId: string; name: string } | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ userId: string; name: string } | null>(null);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('Manual admin credit');
  const [editForm, setEditForm] = useState<any>({});

  // Debounce search so we don't hit the API on every keystroke
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading, isFetching, isError, refetch } = useQuery<PageData>({
    queryKey: [
      'admin-users', page, limit, search, statusFilter, langFilter,
      profileFilter, genderFilter, dateFrom, dateTo, sort, order,
    ],
    queryFn: async () => {
      const params: any = { page, limit, sort, order };
      if (search) params.search = search;
      if (statusFilter === 'blocked') params.isBlocked = true;
      if (statusFilter === 'inactive') { params.isActive = false; params.isBlocked = false; }
      if (statusFilter === 'active') { params.isActive = true; params.isBlocked = false; }
      if (langFilter) params.language = langFilter;
      if (profileFilter) params.isProfileComplete = profileFilter === 'complete';
      if (genderFilter) params.gender = genderFilter;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const r = await getUsers(params);
      return r.data;
    },
    placeholderData: (prev) => prev,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: any) => updateUser(id, data),
    onSuccess: () => { toast.success('User updated'); qc.invalidateQueries({ queryKey: ['admin-users'] }); setEditUser(null); },
    onError: () => toast.error('Update failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteUser(id),
    onSuccess: () => { toast.success('User deleted'); qc.invalidateQueries({ queryKey: ['admin-users'] }); setDeleteModal(null); },
    onError: () => toast.error('Delete failed'),
  });

  const creditMutation = useMutation({
    mutationFn: ({ id, amount, reason }: any) => creditUserWallet(id, Math.round(amount * 100), reason),
    onSuccess: () => {
      toast.success('Wallet credited!');
      setCreditModal(null); setCreditAmount(''); setCreditReason('Manual admin credit');
      qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: () => toast.error('Credit failed'),
  });

  const openEdit = (u: UserRow) => {
    setEditUser(u);
    setEditForm({
      name: u.name || '', email: u.email || '', gender: u.gender || '',
      language: u.language, isActive: u.isActive, isBlocked: u.isBlocked, blockedReason: u.blockedReason || '',
    });
  };

  const users: UserRow[] = data?.data ?? [];
  const pagination = data?.pagination ?? { page: 1, pages: 1, total: 0, limit };
  const summary = pagination.summary || {};

  const stats = [
    { label: 'Total Users', value: (summary.total ?? pagination.total ?? 0).toLocaleString(), icon: <Users size={14} /> },
    { label: 'Active', value: (summary.active ?? 0).toLocaleString(), icon: <UserCheck size={14} />, color: 'text-green-600' },
    { label: 'Blocked', value: (summary.blocked ?? 0).toLocaleString(), icon: <UserX size={14} />, color: 'text-red-600' },
    { label: 'Inactive', value: (summary.inactive ?? 0).toLocaleString(), icon: <Clock size={14} /> },
    { label: 'Profile Done', value: (summary.profileComplete ?? 0).toLocaleString(), icon: <Star size={14} />, color: 'text-blue-600' },
    { label: 'Incomplete', value: (summary.profileIncomplete ?? 0).toLocaleString(), icon: <X size={14} />, color: 'text-orange-600' },
  ];

  const clearFilters = () => {
    setStatusFilter('');
    setLangFilter('');
    setProfileFilter('');
    setGenderFilter('');
    setDateFrom('');
    setDateTo('');
    setSearchInput('');
    setSearch('');
    setSort('createdAt');
    setOrder('desc');
    setPage(1);
  };
  const hasFilters = !!(statusFilter || langFilter || profileFilter || genderFilter || dateFrom || dateTo || search);

  return (
    <div className="p-6">
      <PageHeader title="Users" subtitle="Manage all registered users">
        <ExportButton data={users} filename="users" columns={USER_COLS} />
        <button
          type="button"
          onClick={() => refetch()}
          className="btn-secondary btn-sm"
          disabled={isFetching}
        >
          <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
        </button>
      </PageHeader>

      <OverviewBar items={stats} loading={isLoading} />

      <div className="card p-4 mb-5 space-y-3">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[220px]">
            <label className="block text-[10px] text-gray-500 mb-1">Search</label>
            <SearchInput
              value={searchInput}
              onChange={setSearchInput}
              placeholder="Search name, phone, email…"
            />
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 mb-1">Page size</label>
            <select
              className="input w-24"
              value={limit}
              onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
            >
              {LIMITS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          {hasFilters && (
            <button type="button" onClick={clearFilters} className="text-xs text-red-500 hover:underline flex items-center gap-1 pb-2">
              <X size={12} /> Clear filters
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 pt-3 border-t border-gray-100">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <select className="input" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="blocked">Blocked</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Language</label>
            <select className="input" value={langFilter} onChange={(e) => { setLangFilter(e.target.value); setPage(1); }}>
              <option value="">All</option>
              {LANGS.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Profile</label>
            <select className="input" value={profileFilter} onChange={(e) => { setProfileFilter(e.target.value); setPage(1); }}>
              <option value="">All</option>
              <option value="complete">Complete</option>
              <option value="incomplete">Incomplete</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Gender</label>
            <select className="input" value={genderFilter} onChange={(e) => { setGenderFilter(e.target.value); setPage(1); }}>
              <option value="">All</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Joined from</label>
            <input type="date" className="input" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Joined to</label>
            <input type="date" className="input" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} />
          </div>
        </div>

        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Sort by</label>
            <select className="input w-40" value={sort} onChange={(e) => { setSort(e.target.value); setPage(1); }}>
              <option value="createdAt">Joined date</option>
              <option value="lastActiveAt">Last active</option>
              <option value="name">Name</option>
              <option value="phone">Phone</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Order</label>
            <select className="input w-32" value={order} onChange={(e) => { setOrder(e.target.value as 'asc' | 'desc'); setPage(1); }}>
              <option value="desc">Newest first</option>
              <option value="asc">Oldest first</option>
            </select>
          </div>
          <p className="text-[11px] text-gray-400 pb-2 ml-auto">
            Showing {users.length} of {(pagination.total || 0).toLocaleString()} matched
            {hasFilters ? ' (filtered)' : ''}
          </p>
        </div>
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-5"><TableSkeleton rows={10} cols={7} /></div>
        ) : isError ? (
          <div className="p-8 text-center text-sm text-red-500">
            Failed to load users.{' '}
            <button type="button" className="underline" onClick={() => refetch()}>Retry</button>
          </div>
        ) : users.length === 0 ? (
          <EmptyState message="No users found matching your filters" icon={<Users size={32} />} />
        ) : (
          <Table>
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="table-th">#</th>
                <th className="table-th">User</th>
                <th className="table-th">Phone</th>
                <th className="table-th">Lang</th>
                <th className="table-th">Profile</th>
                <th className="table-th">Status</th>
                <th className="table-th">Joined</th>
                <th className="table-th">Last Active</th>
                <th className="table-th text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, idx) => (
                <tr key={user._id} className="table-row">
                  <td className="table-td text-xs text-gray-400">{(page - 1) * limit + idx + 1}</td>
                  <td className="table-td">
                    <button
                      type="button"
                      onClick={() => navigate(`/users/${user._id}`)}
                      className="flex items-center gap-2.5 text-left hover:opacity-80"
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                        {user.name?.[0]?.toUpperCase() || user.phone.slice(-2)}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 text-xs truncate">{user.name || <span className="text-gray-400 italic">No name</span>}</p>
                        <p className="text-gray-400 text-xs truncate">{user.email || '—'}</p>
                      </div>
                    </button>
                  </td>
                  <td className="table-td font-mono text-xs">{user.phone}</td>
                  <td className="table-td"><span className="badge badge-gray uppercase">{user.language}</span></td>
                  <td className="table-td">
                    <span className={user.isProfileComplete ? 'badge-green' : 'badge-yellow'}>
                      {user.isProfileComplete ? 'Complete' : 'Incomplete'}
                    </span>
                  </td>
                  <td className="table-td">
                    <StatusBadge status={user.isBlocked ? 'blocked' : user.isActive ? 'active' : 'inactive'} />
                  </td>
                  <td className="table-td text-xs whitespace-nowrap">{formatDate(user.createdAt)}</td>
                  <td className="table-td text-xs whitespace-nowrap">{formatDate(user.lastActiveAt)}</td>
                  <td className="table-td">
                    <div className="flex items-center gap-1 justify-end">
                      <button title="Open User Details" onClick={() => navigate(`/users/${user._id}`)} className="p-1.5 rounded hover:bg-blue-50 text-blue-500"><Eye size={14} /></button>
                      <button title="Edit User" onClick={() => openEdit(user)} className="p-1.5 rounded hover:bg-gray-100 text-gray-500"><Edit size={14} /></button>
                      <button
                        title={user.isBlocked ? 'Unblock User' : 'Block User'}
                        onClick={() => updateMutation.mutate({ id: user._id, data: { isBlocked: !user.isBlocked } })}
                        className={`p-1.5 rounded ${user.isBlocked ? 'hover:bg-green-50 text-green-500' : 'hover:bg-red-50 text-red-500'}`}
                      >
                        {user.isBlocked ? <CheckCircle size={14} /> : <Ban size={14} />}
                      </button>
                      <button title="Credit Wallet" onClick={() => setCreditModal({ userId: user._id, name: user.name || user.phone })} className="p-1.5 rounded hover:bg-purple-50 text-purple-500"><Wallet size={14} /></button>
                      <button title="Delete User" onClick={() => setDeleteModal({ userId: user._id, name: user.name || user.phone })} className="p-1.5 rounded hover:bg-red-50 text-red-400"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {(pagination.total > 0) && (
          <div className="p-4 border-t border-gray-100">
            <Pagination
              page={pagination.page || page}
              pages={Math.max(1, pagination.pages || 1)}
              total={pagination.total || 0}
              limit={pagination.limit || limit}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>

      <Modal open={!!editUser} onClose={() => setEditUser(null)} title={`Edit User — ${editUser?.name || editUser?.phone}`} size="md">
        <form onSubmit={(e) => { e.preventDefault(); updateMutation.mutate({ id: editUser!._id, data: editForm }); }} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Name</label><input className="input" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Email</label><input type="email" className="input" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Gender</label>
              <select className="input" value={editForm.gender} onChange={(e) => setEditForm({ ...editForm, gender: e.target.value })}>
                <option value="">—</option><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Language</label>
              <select className="input" value={editForm.language} onChange={(e) => setEditForm({ ...editForm, language: e.target.value })}>
                {LANGS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Blocked Reason</label>
            <input className="input" value={editForm.blockedReason} onChange={(e) => setEditForm({ ...editForm, blockedReason: e.target.value })} placeholder="Reason for blocking (if any)" />
          </div>
          <div className="flex gap-4 pt-1">
            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={editForm.isActive} onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })} /> Active</label>
            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={editForm.isBlocked} onChange={(e) => setEditForm({ ...editForm, isBlocked: e.target.checked })} /> Blocked</label>
          </div>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={() => setEditUser(null)} className="btn-secondary flex-1 justify-center">Cancel</button>
            <button type="submit" disabled={updateMutation.isPending} className="btn-primary flex-1 justify-center">{updateMutation.isPending ? 'Saving…' : 'Save Changes'}</button>
          </div>
        </form>
      </Modal>

      <Modal open={!!creditModal} onClose={() => setCreditModal(null)} title={`Credit Wallet — ${creditModal?.name}`} size="sm">
        <div className="space-y-3">
          <div><label className="block text-xs font-medium text-gray-700 mb-1">Amount (₹)</label><input type="number" min="1" className="input" value={creditAmount} onChange={(e) => setCreditAmount(e.target.value)} placeholder="100" autoFocus /></div>
          <div><label className="block text-xs font-medium text-gray-700 mb-1">Reason</label><input className="input" value={creditReason} onChange={(e) => setCreditReason(e.target.value)} /></div>
          <div className="flex gap-2 pt-2">
            <button onClick={() => setCreditModal(null)} className="btn-secondary flex-1 justify-center">Cancel</button>
            <button onClick={() => creditMutation.mutate({ id: creditModal!.userId, amount: Number(creditAmount), reason: creditReason })} disabled={!creditAmount || creditMutation.isPending} className="btn-primary flex-1 justify-center">{creditMutation.isPending ? 'Crediting…' : 'Credit Wallet'}</button>
          </div>
        </div>
      </Modal>

      <Modal open={!!deleteModal} onClose={() => setDeleteModal(null)} title="Delete User" size="sm">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">Are you sure you want to delete <strong>{deleteModal?.name}</strong>? This will deactivate the account permanently.</p>
          <div className="flex gap-2">
            <button onClick={() => setDeleteModal(null)} className="btn-secondary flex-1 justify-center">Cancel</button>
            <button onClick={() => deleteMutation.mutate(deleteModal!.userId)} disabled={deleteMutation.isPending} className="btn-danger flex-1 justify-center">{deleteMutation.isPending ? 'Deleting…' : 'Delete'}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
